import React, { useEffect, useRef, useCallback } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link, useSearchParams } from "react-router-dom";
import { IoIosArrowForward } from "react-icons/io";
import Products from "../components/products/Products";
import { BsFillGridFill } from "react-icons/bs";
import { FaThList } from "react-icons/fa";
import ShopProducts from "../components/products/ShopProducts";
import { useDispatch, useSelector } from "react-redux";
import { price_range_product, query_products } from "../store/reducers/homeReducer";
import { Helmet } from "react-helmet";
import { PropagateLoader } from "react-spinners";

import { useFilterState } from "../hooks/useFilterState";
import { useTheme } from "../context/ThemeContext";
import {
  CatalogActiveFilters,
  CatalogFilterPanel,
  FILTER_ARRAY_KEYS,
  asFilterArray,
  hasActiveCatalogFilters,
} from "../components/global/ProductFilters";

const URL_ARRAY_FILTERS = ["category", "typeproduct", "typearticle", "brand", "color", "size"];
const URL_VALUE_FILTERS = ["rating", "sortPrice"];
const URL_FILTERS = [...URL_ARRAY_FILTERS, ...URL_VALUE_FILTERS];

const getUrlFilterValues = (searchParams, key) => {
  const values = searchParams.getAll(key)
    .flatMap(value => value.split(","))
    .map(value => value.trim())
    .filter(Boolean);

  return [...new Set(values)];
};

const areSameValues = (first = [], second = []) => {
  if (first.length !== second.length) return false;
  return first.every((value, index) => value === second[index]);
};

const appendFilterValues = (params, key, values) => {
  if (!Array.isArray(values)) return;
  values.filter(Boolean).forEach(value => params.append(key, value));
};

const Shops = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getColor } = useTheme();
  const { currentCompany } = useSelector(state => state.init);
  const { products, categorys, typeproducts, typearticles, brands, colors, sizes, priceRange, latest_product, totalProduct } = useSelector(state => state.home);
  const primaryColor = getColor("primary", "#ae1d22");
  const filterOptions = { categorys, typeproducts, typearticles, brands, colors, sizes };

  const initialUrlFilters = {
    category: getUrlFilterValues(searchParams, "category"),
    typeproduct: getUrlFilterValues(searchParams, "typeproduct"),
    typearticle: getUrlFilterValues(searchParams, "typearticle"),
    brand: getUrlFilterValues(searchParams, "brand"),
    color: getUrlFilterValues(searchParams, "color"),
    size: getUrlFilterValues(searchParams, "size"),
    rating: searchParams.get("rating") || "",
    sortPrice: searchParams.get("sortPrice") || "",
  };

  const { filters, expandedSections, updateFilter, handleCheckboxChange, handleRatingChange, handleResetRating, handlePriceChange, toggleSection, resetFilters } = useFilterState(
    initialUrlFilters,
    { priceRange }
  );

  // Refs para scroll infinito
  const sentinelRef = useRef(null);
  const loadingRef = useRef(false);
  const filtersRef = useRef(filters);
  const syncingFromUrlRef = useRef(false);

  // Mantener filtersRef actualizado sin re-crear el observer
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    const currentFilters = filtersRef.current;
    const updates = [];

    URL_ARRAY_FILTERS.forEach(key => {
      const urlValues = getUrlFilterValues(searchParams, key);
      if (!areSameValues(currentFilters[key] || [], urlValues)) {
        updates.push([key, urlValues]);
      }
    });

    URL_VALUE_FILTERS.forEach(key => {
      const urlValue = searchParams.get(key) || "";
      if ((currentFilters[key] || "") !== urlValue) {
        updates.push([key, urlValue]);
      }
    });

    if (updates.length === 0) return;

    syncingFromUrlRef.current = true;
    updates.forEach(([key, value]) => updateFilter(key, value));
    if (currentFilters.pageNumber !== 1) updateFilter("pageNumber", 1);
    loadingRef.current = false;
  }, [searchParams, updateFilter]);

  const hasMore = products.length < totalProduct;

  const bgStyle = {
    backgroundImage: `url(${currentCompany?.imageDetails})`,
  };

  useEffect(() => {
    dispatch(price_range_product());
  }, [dispatch]);

  // Cuando cambian los filtros (excepto pageNumber), resetear a página 1
  const priceValue0 = filters.priceValues[0];
  const priceValue1 = filters.priceValues[1];
  const categoryFilter = filters.category;
  const typeproductFilter = filters.typeproduct;
  const typearticleFilter = filters.typearticle;
  const brandFilter = filters.brand;
  const colorFilter = filters.color;
  const sizeFilter = filters.size;
  const ratingFilter = filters.rating;
  const sortPriceFilter = filters.sortPrice;
  const categoryStr = JSON.stringify(categoryFilter);
  const typeproductStr = JSON.stringify(typeproductFilter);
  const typearticleStr = JSON.stringify(typearticleFilter);
  const brandStr = JSON.stringify(brandFilter);
  const colorStr = JSON.stringify(colorFilter);
  const sizeStr = JSON.stringify(sizeFilter);

  useEffect(() => {
    if (syncingFromUrlRef.current) {
      syncingFromUrlRef.current = false;
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    URL_FILTERS.forEach(key => nextParams.delete(key));

    appendFilterValues(nextParams, "category", categoryFilter);
    appendFilterValues(nextParams, "typeproduct", typeproductFilter);
    appendFilterValues(nextParams, "typearticle", typearticleFilter);
    appendFilterValues(nextParams, "brand", brandFilter);
    appendFilterValues(nextParams, "color", colorFilter);
    appendFilterValues(nextParams, "size", sizeFilter);
    if (ratingFilter) nextParams.set("rating", ratingFilter);
    if (sortPriceFilter) nextParams.set("sortPrice", sortPriceFilter);

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    categoryFilter,
    typeproductFilter,
    typearticleFilter,
    brandFilter,
    colorFilter,
    sizeFilter,
    ratingFilter,
    sortPriceFilter,
    searchParams,
    setSearchParams,
  ]);
  
  useEffect(() => {
    updateFilter("pageNumber", 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryStr, typeproductStr, typearticleStr, brandStr,
      colorStr, sizeStr, ratingFilter, sortPriceFilter,
      priceValue0, priceValue1]);

  // Cargar productos cuando cambia cualquier filtro incluyendo pageNumber
  useEffect(() => {
    dispatch(
      query_products({
        low: filters.priceValues[0],
        high: filters.priceValues[1],
        category: filters.category.length > 0 ? filters.category : undefined,
        typeproduct: filters.typeproduct.length > 0 ? filters.typeproduct : undefined,
        typearticle: filters.typearticle.length > 0 ? filters.typearticle : undefined,
        brand: filters.brand.length > 0 ? filters.brand : undefined,
        color: filters.color.length > 0 ? filters.color : undefined,
        size: filters.size.length > 0 ? filters.size : undefined,
        rating: filters.rating,
        sortPrice: filters.sortPrice,
        pageNumber: filters.pageNumber,
      })
    );
    loadingRef.current = false;
  }, [dispatch, filters]);

  // IntersectionObserver para scroll infinito
  const loadMore = useCallback(() => {
    if (loadingRef.current) return;
    const f = filtersRef.current;
    const currentCount = products.length;
    if (currentCount >= totalProduct) return;
    loadingRef.current = true;
    updateFilter("pageNumber", f.pageNumber + 1);
  }, [products.length, totalProduct, updateFilter]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  // Función para quitar un filtro específico
  const removeFilter = (filterKey, value = "") => {
    // Si es un filtro de array, quitar el valor específico
    if (FILTER_ARRAY_KEYS.includes(filterKey) && value) {
      const currentValues = asFilterArray(filters[filterKey]);
      updateFilter(filterKey, currentValues.filter(v => v !== value));
    } else if (FILTER_ARRAY_KEYS.includes(filterKey)) {
      // Si no hay valor, limpiar todo el array
      updateFilter(filterKey, []);
    } else {
      // Resetear el filtro en el estado
      updateFilter(filterKey, "");
    }

    // Si es el filtro de rating, usar la función específica
    if (filterKey === "rating") {
      handleResetRating();
    }

    // Si es el filtro de precio, restaurar valores originales
    if (filterKey === "priceValues" && priceRange) {
      handlePriceChange([priceRange.low, priceRange.high]);
    }
  };

  // Botón para limpiar todos los filtros
  const clearAllFilters = () => {
    resetFilters();

    // Restaurar precio por defecto
    if (priceRange?.low && priceRange?.high) {
      handlePriceChange([priceRange.low, priceRange.high]);
    }
  };

  const hasActiveFilters = hasActiveCatalogFilters(filters, priceRange);

  return (
    <div>
      <Header />
      <Helmet>
        <title>Tienda | Tekos.pe</title>
        <meta name="description" content="Compra zapatillas Adidas, Puma al mejor precio en Comprame. Envíos a todo Perú." />
        <meta name="keywords" content="zapatillas, Adidas, ofertas, calzado deportivo" />
      </Helmet>

      {currentCompany?.verimageDetails && (
        <section className="h-[220px] mt-6 bg-cover bg-no-repeat relative bg-left" style={bgStyle}>
          <div className="absolute left-0 top-0 w-full h-full bg-[#2422228a]">
            <div className="w-[85%] md:w-[80%] sm:w-[90%] lg:w-[90%] h-full mx-auto">
              <div className="flex flex-col justify-center gap-1 items-center h-full w-full text-white">
                <h2 className="text-3xl font-bold">Tienda</h2>
                <div className="flex justify-center items-center gap-2 text-2xl w-full">
                  <Link to="/" className="hover:text-gray-200 transition-colors">
                    Inicio
                  </Link>
                  <span className="pt-1">
                    <IoIosArrowForward />
                  </span>
                  <span>Tienda</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="py-8">
        <div className="w-[85%] md:w-[80%] sm:w-full sm:px-2 lg:w-[90%] h-full mx-auto">
          <CatalogActiveFilters
            filters={filters}
            priceRange={priceRange}
            onRemove={removeFilter}
            onClear={clearAllFilters}
          />
          {/* Barra Filtrar/Ordenar - solo móvil sticky */}
          <div className="hidden md:block sticky top-[110px] z-40 bg-white border-b border-gray-200 shadow-sm">
            <div className="grid grid-cols-2 divide-x divide-gray-200">
              <button
                onClick={() => updateFilter("filterVisible", !filters.filterVisible)}
                className="flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-slate-700 active:bg-gray-50">
                <svg className="w-4 h-4" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                {filters.filterVisible ? "Ocultar" : "Filtrar"}
                {hasActiveFilters && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: primaryColor }} />}
              </button>
              <div className="flex items-center justify-center">
                <select
                  onChange={e => updateFilter("sortPrice", e.target.value)}
                  value={filters.sortPrice}
                  className="w-full h-full py-2.5 px-3 text-sm font-semibold text-slate-700 bg-transparent outline-none appearance-none text-center"
                  aria-label="Ordenar productos">
                  <option value="">Ordenar</option>
                  <option value="low-to-high">Precio: Menor</option>
                  <option value="high-to-low">Precio: Mayor</option>
                </select>
              </div>
            </div>
          </div>

          {/* Sidebar filtros móvil */}
          {!filters.filterVisible && (
            <div className="hidden md:block fixed inset-0 z-50">
              {/* Fondo oscuro */}
              <div
                className="absolute inset-0 bg-black/50"
                onClick={() => updateFilter("filterVisible", true)}
              />
              {/* Panel lateral */}
              <div className="absolute left-0 top-0 h-full w-3/4 bg-white shadow-xl overflow-y-auto">
                {/* Header sidebar */}
                <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
                  <span className="font-bold text-slate-700 text-sm">Filtros</span>
                  <div className="flex items-center gap-3">
                    {hasActiveFilters && (
                      <button onClick={clearAllFilters} className="text-xs font-medium" style={{ color: primaryColor }}>
                        Limpiar
                      </button>
                    )}
                    <button
                      onClick={() => updateFilter("filterVisible", true)}
                      className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100">
                      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                {/* Contenido filtros */}
                <CatalogFilterPanel
                  className="px-4 py-3"
                  filterOptions={filterOptions}
                  filters={filters}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                  onCheckboxChange={handleCheckboxChange}
                  priceRange={priceRange}
                  onPriceChange={handlePriceChange}
                  showRating={Boolean(currentCompany?.verfiltroStars)}
                  onRatingChange={handleRatingChange}
                  onResetRating={() => removeFilter("rating")}
                  idPrefix="shop-mobile"
                />                {/* Botón aplicar */}
                <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3">
                  <button
                    onClick={() => updateFilter("filterVisible", true)}
                    className="w-full py-3 text-white font-semibold rounded-lg text-sm"
                    style={{ backgroundColor: primaryColor }}>
                    Ver {totalProduct} producto{totalProduct !== 1 ? "s" : ""}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="w-full flex flex-wrap">
            <div className={`w-3/12 md-lg:w-4/12 md:hidden pr-8 ${
              filters.filterVisible ? "" : "md-lg:hidden"
            }`}>
              <CatalogFilterPanel
                filterOptions={filterOptions}
                filters={filters}
                expandedSections={expandedSections}
                toggleSection={toggleSection}
                onCheckboxChange={handleCheckboxChange}
                priceRange={priceRange}
                onPriceChange={handlePriceChange}
                showRating={Boolean(currentCompany?.verfiltroStars)}
                onRatingChange={handleRatingChange}
                onResetRating={() => removeFilter("rating")}
                idPrefix="shop-desktop"
              />
              {currentCompany?.vercarruselLatestProducts && latest_product?.length > 0 && (
                <div className="py-5 flex flex-col gap-4 md:hidden mt-6">
                  <Products title="Últimos productos" products={latest_product} />
                </div>
              )}
            </div>

            <div className="w-9/12 md-lg:w-8/12 md:w-full">
              <div className="pl-8 md:pl-0">

                {/* Header resultados - solo desktop */}
                <div className="py-3 bg-white mb-4 px-3 rounded-lg flex justify-between items-center border border-gray-200 shadow-sm md:hidden">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-800">
                      {totalProduct} Producto{totalProduct !== 1 ? "s" : ""} Disponible{totalProduct !== 1 ? "s" : ""}
                    </h2>
                    {hasActiveFilters && <p className="text-sm text-gray-500 mt-1">Filtrados según tu selección</p>}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <select
                        onChange={e => updateFilter("sortPrice", e.target.value)}
                        className="p-2.5 pl-10 border border-gray-300 outline-0 text-slate-700 font-medium rounded-lg appearance-none bg-white pr-10"
                        value={filters.sortPrice}
                        aria-label="Ordenar productos">
                        <option value="">Ordenar por</option>
                        <option value="low-to-high">Precio: Bajo a Alto</option>
                        <option value="high-to-low">Precio: Alto a Bajo</option>
                      </select>
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 md-lg:hidden bg-gray-100 p-1 rounded-lg">
                      <button onClick={() => updateFilter("styles", "grid")}
                        className={`p-2.5 rounded-md transition-all ${filters.styles === "grid" ? "bg-white shadow-sm" : "text-gray-500"}`}
                        style={filters.styles === "grid" ? { color: primaryColor } : undefined}>
                        <BsFillGridFill size={18} />
                      </button>
                      <button onClick={() => updateFilter("styles", "list")}
                        className={`p-2.5 rounded-md transition-all ${filters.styles === "list" ? "bg-white shadow-sm" : "text-gray-500"}`}
                        style={filters.styles === "list" ? { color: primaryColor } : undefined}>
                        <FaThList size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="pb-8">
                  {products?.length > 0 ? (
                    <ShopProducts products={products} styles={filters.styles} />
                  ) : (
                    <div className="text-center py-16">
                      <div className="max-w-md mx-auto">
                        <svg className="w-24 h-24 text-gray-300 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        <h3 className="text-2xl font-semibold text-gray-700 mb-3">No se encontraron productos</h3>
                        <p className="text-gray-500 mb-6">No hay productos que coincidan con los filtros seleccionados. Intenta con otros criterios de búsqueda.</p>
                        {hasActiveFilters && (
                          <button onClick={clearAllFilters} className="px-6 py-3 text-white font-medium rounded-lg transition-opacity hover:opacity-90" style={{ backgroundColor: primaryColor }}>
                            Limpiar todos los filtros
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Sentinel para scroll infinito */}
                <div ref={sentinelRef} className="w-full h-4" />

                {/* Indicador de carga */}
                {hasMore && (
                  <div className="flex justify-center py-6">
                    <PropagateLoader color={primaryColor} size={12} />
                  </div>
                )}

                {/* Mensaje fin de resultados */}
                {!hasMore && products.length > 0 && (
                  <p className="text-center text-gray-400 text-sm py-6">
                    — {totalProduct} producto{totalProduct !== 1 ? 's' : ''} mostrado{totalProduct !== 1 ? 's' : ''} —
                  </p>
                )}

                {/* Últimos productos en versión móvil */}
                {currentCompany?.vercarruselLatestProducts && latest_product?.length > 0 && (
                  <div className="py-8 md:block hidden">
                    <Products title="Productos Recientes" products={latest_product} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Shops;
